import { setNotification } from "features/Notification/notificationSlice";
import { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";

export default function BulkWProductDelete({ allProducts = [], refreshFunc }) {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleSelectAll = (checked, ids = []) => {
    if (checked)
      setSelectedIds((prev) => Array.from(new Set([...(prev || []), ...ids])));
    else
      setSelectedIds((prev) => (prev || []).filter((id) => !ids.includes(id)));
  };

  const filteredProducts = useMemo(() => {
    const q = (searchQuery || "").toString().trim().toLowerCase();
    if (!q) return allProducts || [];
    return (allProducts || []).filter((p) => {
      const prod = p || {};
      return (
        String(prod.sortId || "")
          .toLowerCase()
          .includes(q) ||
        (prod.productStyle || "").toLowerCase().includes(q) ||
        (prod.productColor || "").toLowerCase().includes(q) ||
        (prod.productDescription || "").toLowerCase().includes(q) ||
        (prod.teamName || "").toLowerCase().includes(q) ||
        (prod.productType || "").toLowerCase().includes(q) ||
        (prod.id ? String(prod.id) : "").toLowerCase().includes(q)
      );
    });
  }, [allProducts, searchQuery]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const selectedProducts = useMemo(
    () => (allProducts || []).filter((p) => selectedIds.includes(p.id)),
    [allProducts, selectedIds],
  );

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    setLoading(true);
    try {
      const { data } = await API.post(
        "/wo-products/bulk-delete",
        { ids: selectedIds },
        { headers: authHeader() },
      );

      if (data?.message?.type === "success" || data?.success) {
        dispatch(
          setNotification({ message: "Products deleted", type: "success" }),
        );
        setSelectedIds([]);
        setShowModal(false);
        if (refreshFunc) await refreshFunc();
      } else {
        dispatch(
          setNotification({
            message: data?.message?.text || "Delete failed",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || "Delete failed",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-sm py-0 w-100 text-start ps-0"
        onClick={() => setShowModal(true)}
      >
        Bulk Delete Products
      </button>

      <Modal
        size="lg"
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setConfirming(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk Delete Products</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!confirming ? (
            <div>
              <div className="d-flex align-items-center mb-2">
                <input
                  type="search"
                  placeholder="Search by item, style, color, description, team"
                  className="form-control form-control-sm me-2"
                  style={{ minWidth: 240 }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="small text-muted text-nowrap">
                  Showing {filteredProducts.length} of{" "}
                  {(allProducts || []).length}
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}>
                        <input
                          type="checkbox"
                          checked={
                            selectedIds.length ===
                              (filteredProducts || []).length &&
                            (filteredProducts || []).length > 0
                          }
                          onChange={(e) =>
                            toggleSelectAll(
                              e.target.checked,
                              (filteredProducts || []).map((p) => p.id),
                            )
                          }
                        />
                      </th>
                      <th>Preview</th>
                      <th>Item#</th>
                      <th>Type</th>
                      <th>Style</th>
                      <th>Color</th>
                      <th>Description</th>
                      <th>Team</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(filteredProducts || []).map((p) => (
                      <tr key={p.id} onClick={() => toggleSelect(p.id)}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(p.id)}
                          />
                        </td>
                        <td style={{ width: 120 }}>
                          {p.productImage ? (
                            <ImageWIthSignedUrl
                              path={p.productImage}
                              alt={p.productDescription || ""}
                              imgStyle={{ width: "100%", height: 60 }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 60,
                                background: "#f1f1f1",
                              }}
                            />
                          )}
                        </td>
                        <td className="align-middle">{p.sortId}</td>
                        <td className="align-middle">{p.productType}</td>
                        <td className="align-middle">{p.productStyle}</td>
                        <td className="align-middle">{p.productColor}</td>
                        <td className="align-middle">{p.productDescription}</td>
                        <td className="align-middle">{p.teamName}</td>
                        <td className="align-middle">
                          {p.quantity ?? p.totalQuantity ?? p.qty ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-2">
                <strong>Confirm Deletion</strong>
                <div className="text-muted small">
                  The following products will be deleted:
                </div>
              </div>

              <div
                className="table-responsive"
                style={{ maxHeight: 300, overflow: "auto" }}
              >
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Item#</th>
                      <th>Type</th>
                      <th>Style</th>
                      <th>Color</th>
                      <th>Description</th>
                      <th>Team</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts.map((p) => (
                      <tr key={p.id}>
                        <td style={{ width: 120 }}>
                          {p.productImage ? (
                            <ImageWIthSignedUrl
                              path={p.productImage}
                              alt={p.productDescription || ""}
                              imgStyle={{ width: "100%", height: 60 }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 60,
                                background: "#f1f1f1",
                              }}
                            />
                          )}
                        </td>
                        <td className="align-middle">{p.sortId}</td>
                        <td className="align-middle">{p.productType}</td>
                        <td className="align-middle">{p.productStyle}</td>
                        <td className="align-middle">{p.productColor}</td>
                        <td className="align-middle">{p.productDescription}</td>
                        <td className="align-middle">{p.teamName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              if (confirming) setConfirming(false);
              else setShowModal(false);
            }}
          >
            {confirming ? "Cancel" : "Close"}
          </button>

          {!confirming ? (
            <button
              className="btn btn-danger"
              onClick={() => setConfirming(true)}
              disabled={!selectedIds.length}
            >
              Delete Selected ({selectedIds.length})
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Confirm Delete"}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
